"use client";

import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stickers = [{
    label: "CANON PRO 6600",
    rotate: "-rotate-4",
    bg: "bg-blue-600 text-white",
    top: "-top-5 left-[8%]"
  }, {
    label: "PREMIUM QUALITY",
    rotate: "rotate-3",
    bg: "bg-red-700 text-white",
    top: "-top-7 left-1/2 -translate-x-1/2"
  }, {
    label: "RIYADH STUDIO",
    rotate: "-rotate-2",
    bg: "bg-slate-200",
    top: "-top-5 right-[8%]"
  }];
  return <section data-section-id="1717" id="herocarousel" className="relative bg-slate-50 overflow-hidden">
      <div className="relative max-w-[1360px] mx-auto px-8 lg:px-12 py-24 md:py-32">
        {}
        <div ref={headRef} className={`flex items-center gap-3 mb-12 md:mb-16 transition-all duration-700 ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="font-mono text-xs md:text-sm font-medium tracking-[0.18em] uppercase text-blue-900">
            § 01 / PHOTO GLAMOUR
          </span>
          <span className="h-px w-20 bg-blue-900" />
        </div>

        {}
        <div className="relative text-center max-w-5xl mx-auto">
          {}
          <div className="hidden md:block absolute -top-6 -left-6 lg:-left-16 text-blue-900" aria-hidden="true">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M32 8 L36 28 L56 32 L36 36 L32 56 L28 36 L8 32 L28 28 Z" />
            </svg>
          </div>

          {}
          <div className="hidden md:block absolute -top-4 -right-4 lg:-right-20 text-blue-900 opacity-90" aria-hidden="true">
            <svg width="90" height="90" viewBox="0 0 90 90" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="35" cy="35" r="22" />
              <circle cx="65" cy="55" r="14" />
              <circle cx="28" cy="65" r="9" />
              <circle cx="30" cy="28" r="3" fill="currentColor" />
              <circle cx="60" cy="50" r="2" fill="currentColor" />
            </svg>
          </div>

          <h1 className={`font-default font-bold text-blue-900 leading-[0.92] tracking-tight transition-all duration-700 ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          fontSize: "clamp(3.5rem, 9vw, 8.5rem)"
        }}>
            <span className="block">Capture Moments.</span>
            <span className="block">
              Create{" "}
              <span className="relative inline-block">
                <span className="absolute inset-x-[-6px] top-[18%] bottom-[8%] bg-blue-600 -rotate-1 -z-0" aria-hidden="true" />
                <span className="relative z-10 text-white">Art.</span>
              </span>
            </span>
          </h1>

          <p className={`mt-8 md:mt-10 mx-auto max-w-xl text-slate-700 font-default text-lg md:text-xl leading-relaxed transition-all duration-700 delay-150 ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            Professional photography and industrial printing in Riyadh. From cinematic videography to custom large-scale prints, we bring your vision to life.
          </p>

          {}
          <div className={`mt-10 md:mt-12 flex flex-col sm:flex-row items-center justify-center gap-5 transition-all duration-700 delay-300 ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Button asChild className="bg-blue-600 text-white hover:bg-blue-700 border-[3px] border-blue-900 rounded-lg font-default font-bold text-base px-8 py-6 h-auto hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-lg transition-all duration-150">
              <Link to="/StudioServicesPage">
                <Sparkles className="size-5" />
                Book Your Session
              </Link>
            </Button>
            <Button asChild className="bg-white text-blue-900 hover:bg-slate-100 border-[3px] border-blue-900 rounded-lg font-default font-bold text-base px-8 py-6 h-auto hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-lg transition-all duration-150">
              <Link to="/ArtDecorShopPage">
                Shop Decor
                <ArrowRight className="size-5" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div ref={imgRef} className={`relative mt-20 md:mt-28 transition-all duration-700 delay-200 ${imgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="absolute inset-x-0 -top-2 md:top-0 z-20 pointer-events-none hidden md:block">
            {stickers.map((s, i) => <div key={i} data-index={i} className={`absolute ${s.top} ${s.rotate} ${s.bg} border-[2px] border-blue-900 rounded-md px-4 py-2 font-default text-sm md:text-base shadow-md whitespace-nowrap`}>
                {s.label}
              </div>)}
          </div>

          {}
          <div className="flex md:hidden flex-wrap justify-center gap-3 mb-6">
            {stickers.slice(0, 2).map((s, i) => <div key={i} data-index={i} className={`${s.rotate} ${s.bg} border-[2px] border-blue-900 rounded-md px-3 py-1.5 font-default text-xs shadow-md whitespace-nowrap`}>
                {s.label}
              </div>)}
          </div>

          {}
          <div className="relative border-[4px] border-blue-900 rounded-lg overflow-hidden bg-blue-100">
            <div className="aspect-[16/9] w-full">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555315/img/canon_pro_printing.png" alt="Canon Pro 6600 printer creating high-quality large scale prints" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>

          {}
          <div className="hidden md:block absolute -bottom-10 -left-8 lg:-left-16 text-blue-900 -rotate-12" aria-hidden="true">
            <svg width="140" height="140" viewBox="0 0 140 140" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M70 130 C70 130 70 90 60 70 C50 50 30 40 20 30" />
              <path d="M55 80 C40 75 25 70 15 55 C28 55 42 60 55 70" fill="blue" fillOpacity="0.2" />
              <path d="M62 95 C45 92 30 85 18 72 C32 70 48 75 62 85" fill="blue" fillOpacity="0.2" />
              <path d="M68 110 C50 108 35 100 22 88 C36 87 54 92 68 100" fill="blue" fillOpacity="0.2" />
              <path d="M58 65 C45 60 32 50 22 35 C36 36 50 45 60 55" fill="blue" fillOpacity="0.2" />
            </svg>
          </div>

          {}
          <div className="hidden md:block absolute -bottom-6 -right-4 lg:-right-12 text-blue-600" aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <path d="M28 6 L28 22 M28 34 L28 50 M6 28 L22 28 M34 28 L50 28" />
              <path d="M12 12 L20 20 M36 36 L44 44 M12 44 L20 36 M36 20 L44 12" />
            </svg>
          </div>
        </div>
      </div>

      {}
      <div className="relative h-4 w-full" aria-hidden="true">
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 16" fill="rgb(30 58 138)">
          <polygon points="0,0 4,16 8,0 12,16 16,0 20,16 24,0 28,16 32,0 36,16 40,0 44,16 48,0 52,16 56,0 60,16 64,0 68,16 72,0 76,16 80,0 84,16 88,0 92,16 96,0 100,16 100,0" />
        </svg>
      </div>
    </section>;
}